import { z } from "zod";
import { TemplateParameterKind } from "@gen/tracker/v1/flow_template_pb";

// Lightweight Zod schemas for the flow-template UX. The TS-side editor and
// apply wizard use these to keep validation messages consistent and to keep
// a single source of truth for "is this template / binding map shippable?".

export const TemplateNameSchema = z
  .string()
  .min(1, "Укажите название шаблона")
  .max(120, "Слишком длинное название (до 120 символов)");

export const ParameterKeySchema = z
  .string()
  .min(1, "Укажите ключ параметра")
  .max(64, "Слишком длинный ключ параметра")
  .regex(
    /^[a-z][a-z0-9_]*$/,
    "Строчные латинские буквы, цифры и _; начинается с буквы",
  );

export const TemplateParameterSchema = z.object({
  key: ParameterKeySchema,
  label: z.string().max(120).optional().default(""),
  description: z.string().max(280).optional().default(""),
  defaultValue: z.string().optional().default(""),
  kind: z.nativeEnum(TemplateParameterKind),
});

export const TemplateParametersSchema = z
  .array(TemplateParameterSchema)
  .superRefine((params, ctx) => {
    const seen = new Set<string>();
    params.forEach((p, i) => {
      if (seen.has(p.key)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [i, "key"],
          message: `Duplicate parameter key: ${p.key}`,
        });
      }
      seen.add(p.key);
    });
  });

/**
 * Build a per-template binding schema. Each parameter must resolve to a
 * non-empty string (after falling back to its default). Lander/Offer
 * parameters additionally enforce a UUID shape so we fail fast instead of
 * round-tripping a 400 from InstantiateFlowTemplate.
 */
export function buildBindingsSchema(
  params: { key: string; kind: TemplateParameterKind; defaultValue?: string }[],
) {
  const shape: Record<string, z.ZodTypeAny> = {};
  for (const p of params) {
    const isUUID =
      p.kind === TemplateParameterKind.LANDER || p.kind === TemplateParameterKind.OFFER;
    shape[p.key] = z
      .string()
      .transform((v) => (v?.trim() ? v.trim() : (p.defaultValue ?? "").trim()))
      .superRefine((v, ctx) => {
        if (!v) {
          ctx.addIssue({ code: z.ZodIssueCode.custom, message: "binding required" });
          return;
        }
        if (isUUID && !isUUIDLike(v) && !isParamPlaceholder(v)) {
          ctx.addIssue({ code: z.ZodIssueCode.custom, message: "must be a UUID" });
        }
      });
  }
  return z.object(shape);
}

/**
 * UUID guard reused by pickers / canvas form cells. Returns null when the
 * value is a `${param}` placeholder so callers can render the chip differently.
 */
export function isUUIDLike(value: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);
}

export function isParamPlaceholder(value: string): boolean {
  return /^\$\{[^}]+\}$/.test(value);
}
